/**
 * HC Product Variant Picker V2
 * Clean dropdown-based variant picker for Shopify product pages.
 * Integrates with the Broadcast theme via theme:variant:change event.
 * Supports cross-product size navigation via AJAX Section Rendering API.
 */

class HCVariantPickerV2 extends HTMLElement {
  constructor() {
    super();
    this._isNavigating = false;
    this._abortController = null;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productFormId = this.dataset.productFormId;
    this.variants = this._parseVariants();

    if (!this.variants) return;

    this._bindEvents();
    this._setupPopStateHandler();
    this._syncInitialVariant();

    // Auto-redirect to sibling if current product lacks preferred material
    const autoRedirect = this.dataset.autoRedirect;
    if (autoRedirect && !window._hcV2SkipAutoRedirect) {
      setTimeout(() => this._navigateToSibling(autoRedirect), 150);
    }
    delete window._hcV2SkipAutoRedirect;
  }

  // ─── Private Helpers ─────────────────────────────────────────

  _parseVariants() {
    const script = this.querySelector('[data-variants-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[HCVariantPickerV2] Failed to parse variant JSON', e);
      return null;
    }
  }

  _bindEvents() {
    this.querySelectorAll('select.hc-vpv2__select').forEach(select => {
      select.addEventListener('change', (e) => this._onSelectChange(e));
    });
  }

  _syncInitialVariant() {
    const variant = this._findMatchingVariant();
    if (variant) {
      this._applyVariant(variant);
    }
  }

  _onSelectChange(e) {
    const select = e.target;
    const selectedOption = select.options[select.selectedIndex];

    if (selectedOption && selectedOption.dataset.siblingUrl) {
      this._navigateToSibling(selectedOption.dataset.siblingUrl);
      return;
    }

    const variant = this._findMatchingVariant();
    if (!variant) return;
    this._applyVariant(variant);
    this._updateURL(variant);
  }

  /**
   * Build the current selections array from all <select> elements,
   * ordered by data-option-index, then find the matching variant.
   */
  _findMatchingVariant() {
    const selects = Array.from(this.querySelectorAll('select.hc-vpv2__select'))
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));

    const selectedOptions = selects.map(s => s.value);

    return this.variants.find(v => {
      return selectedOptions.every((val, idx) => {
        return v[`option${idx + 1}`] === val;
      });
    }) || null;
  }

  /**
   * Update the hidden form input, dispatch theme:variant:change,
   * and update the ATC button / price block directly.
   */
  _applyVariant(variant) {
    this._updateFormInput(variant.id);
    this._dispatchVariantChange(variant);
    this._updateAddToCartButton(variant);
    this._updatePriceBlock(variant);
    [100, 500, 1500].forEach(ms => {
      setTimeout(() => {
        this._updateAddToCartButton(variant);
        this._updatePriceBlock(variant);
      }, ms);
    });
  }

  _updateFormInput(variantId) {
    // Target by product form ID (Broadcast pattern: ProductForm--{sectionId}-{productId})
    const byForm = this.productFormId
      ? document.querySelector(`input[name="id"][form="${this.productFormId}"]`)
      : null;

    // Fallback: find hidden id input inside the nearest product form
    const bySection = this.closest('[data-section-id]')
      ? this.closest('[data-section-id]').querySelector('[data-product-form] input[name="id"], .product__form input[name="id"]')
      : null;

    const input = byForm || bySection;
    if (input) {
      input.value = variantId;
    }
  }

  _dispatchVariantChange(variant) {
    const container = this.closest('.shopify-section') || this.closest('[data-section-id]') || this.parentElement;

    if (container) {
      container.dispatchEvent(
        new CustomEvent('theme:variant:change', {
          detail: { variant },
          bubbles: true,
        })
      );
    }
  }

  _updateAddToCartButton(variant) {
    const container = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (!container) return;

    const btn = container.querySelector('[data-add-to-cart], button[name="add"]');
    if (!btn) return;

    const btnText = btn.querySelector('.btn__text, [data-add-to-cart-text]');
    const isPreorder = btn.dataset.preorder === 'true';
    const preorderStr = btn.dataset.preorderText || 'Pre-Order';
    const addStr = isPreorder
      ? preorderStr
      : ((window.theme && window.theme.strings && window.theme.strings.addToCart) || 'Add to Cart');
    const soldStr = (window.theme && window.theme.strings && window.theme.strings.soldOut) || 'Sold Out';
    const formWrapper = container.querySelector('[data-form-wrapper]');
    const productSubmit = container.querySelector('.product__submit');

    if (variant.available) {
      btn.disabled = false;
      btn.classList.remove('btn--sold-out', 'disabled');
      if (btnText) btnText.textContent = addStr;
      if (formWrapper) formWrapper.classList.remove('variant--soldout', 'variant--unavailable');
      if (productSubmit) productSubmit.classList.remove('variant--soldout', 'variant--unavailable');
    } else {
      btn.disabled = true;
      btn.classList.add('btn--sold-out');
      if (btnText) btnText.textContent = soldStr;
      if (formWrapper) formWrapper.classList.add('variant--soldout');
      if (productSubmit) productSubmit.classList.add('variant--soldout');
    }
  }

  _updatePriceBlock(variant) {
    const container = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (!container) return;

    const priceEl = container.querySelector('[data-product-price]');
    const comparePriceEl = container.querySelector('[data-compare-price]');
    const comparePriceText = container.querySelector('[data-compare-text]');
    const priceWrapper = container.querySelector('.product__price, [data-product-price-wrapper]');

    if (priceEl && variant.price !== undefined) {
      priceEl.innerHTML = this._formatMoney(variant.price);
    }

    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (comparePriceEl) {
      if (hasDiscount) {
        comparePriceEl.innerHTML = this._formatMoney(variant.compare_at_price);
        comparePriceEl.classList.remove('hidden');
      } else {
        comparePriceEl.classList.add('hidden');
      }
    }

    if (comparePriceText) {
      comparePriceText.classList.toggle('hidden', !hasDiscount);
    }

    if (priceWrapper) {
      priceWrapper.classList.toggle('sale', hasDiscount);
    }
  }

  _updateURL(variant) {
    if (!window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', url.toString());
  }

  // ─── Sibling Navigation (AJAX Section Swap) ──────────────────

  async _navigateToSibling(productUrl) {
    try {
      const url = new URL(productUrl, window.location.origin);
      if (url.origin !== window.location.origin) return;
      productUrl = url.pathname;
    } catch (e) {
      return;
    }

    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    if (this._isNavigating) return;
    this._isNavigating = true;

    const sectionWrapper = this.closest('.shopify-section');
    if (!sectionWrapper) {
      window.location.href = productUrl;
      return;
    }

    sectionWrapper.style.transition = 'opacity 0.2s ease';
    sectionWrapper.style.opacity = '0.4';
    sectionWrapper.style.pointerEvents = 'none';

    try {
      const res = await fetch(productUrl, { signal: this._abortController.signal });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const siblingProduct = doc.querySelector('[data-section-type="product"]');
      const siblingWrapper = siblingProduct ? siblingProduct.closest('.shopify-section') : null;

      if (!siblingWrapper) throw new Error('Product section not found in sibling page');

      window._hcV2SkipAutoRedirect = true;

      sectionWrapper.innerHTML = siblingWrapper.innerHTML;

      sectionWrapper.querySelectorAll('script:not([src])').forEach(oldScript => {
        try {
          const newScript = document.createElement('script');
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        } catch (e) {
          // Non-JS script types (JSON-LD, etc.)
        }
      });

      sectionWrapper.style.opacity = '1';
      sectionWrapper.style.pointerEvents = '';

      history.pushState({ hcSiblingNav: true, productUrl }, '', productUrl);

      this._reinitGallery(sectionWrapper);

      if (window.hcJudgemeBadgeReinit) window.hcJudgemeBadgeReinit();
      if (window.hcSmsDealReveal) window.hcSmsDealReveal();

    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[HCVariantPickerV2] Sibling navigation failed:', err);
      sectionWrapper.style.opacity = '1';
      sectionWrapper.style.pointerEvents = '';
      window.location.href = productUrl;
    } finally {
      this._isNavigating = false;
      this._abortController = null;
    }
  }

  _reinitGallery(sectionWrapper) {
    const resizeEvt = () => document.dispatchEvent(new CustomEvent('theme:resize:width'));

    requestAnimationFrame(() => {
      if (window.hcProductGrid3Init) window.hcProductGrid3Init();

      resizeEvt();

      const images = sectionWrapper.querySelectorAll('product-images img[src]');
      let loaded = 0;
      const total = images.length;

      if (total === 0) return;

      const onLoad = () => {
        loaded++;
        if (loaded >= Math.min(total, 4)) resizeEvt();
      };

      images.forEach(img => {
        if (img.complete) { onLoad(); return; }
        img.addEventListener('load', onLoad, { once: true });
      });

      setTimeout(resizeEvt, 600);
    });
  }

  _setupPopStateHandler() {
    if (window._hcV2PopStateRegistered) return;
    window._hcV2PopStateRegistered = true;

    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.hcSiblingNav) {
        window.location.reload();
      }
    });
  }

  _formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }
}

if (!customElements.get('hc-variant-picker-v2')) {
  customElements.define('hc-variant-picker-v2', HCVariantPickerV2);
}
